import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StoreFormProps } from './types';
import Step1 from '../shared/CustomerFormSteps/Step1';
import { Step1Props } from '../shared/CustomerFormSteps/Step1/types';
import Step2 from './Step2';
import { Step2Props } from './Step2/types';
import Step3 from './Step3';
import { Step3Props } from './Step3/types';
import Step4 from '../shared/CustomerFormSteps/Step4';
import { Step4Props } from '../shared/CustomerFormSteps/Step4/types';
import Step5 from '../shared/CustomerFormSteps/Step5';
import { Step5Props } from '../shared/CustomerFormSteps/Step5/types';
import {
    Step1ValueModel,
    Step4ValueModel,
    Step5ValueModel,
} from '../shared/CustomerFormSteps/types';
import { Step2ValueModel, Step3ValueModel } from './types';
import { getSubmitStoreRegistrationBody, getSections } from './utils';
import { fetchMunicipalities } from '../shared/utils';
import CompanyRegistrationsService from "@apps/Shared/Services/CompanyRegistrations/CompanyRegistrations.service";
import { StatusCode } from "@apps/Shared/Services/Response.types";
import { IMunicipality } from "@apps/Shared/Services/CompanyRegistrations/interfaces";
import Help from "@sharedComponents/Help";
import PantaForm from "@sharedComponents/PantaForm";
import PantaFormStep from "@sharedComponents/PantaForm/PantaFormStep";

function submit(
    myPagesRoute: string,
    setSubmitting: Function,
    setRequestErrors: Function,
    setUnexpectedErrors: Function,
    values: Step1ValueModel &
        Step2ValueModel &
        Step3ValueModel &
        Step4ValueModel &
        Step5ValueModel
) {
    const body = getSubmitStoreRegistrationBody(values)

    setSubmitting(true)
    setUnexpectedErrors([])

    CompanyRegistrationsService.SubmitStoreRegistration(body)
        .then(result => {
            if (result.status === StatusCode.OK) {
                if (window.location.pathname === myPagesRoute) {
                    window.location.reload()
                } else {
                    window.location.href = myPagesRoute;
                }
            } else if (result.status === StatusCode.BAD_REQUEST) {
                if (result.data.message) {
                    setRequestErrors(result.data.message)
                } else {
                    setRequestErrors({})
                }

                setSubmitting(false)
            } else {
                const errorMessages = result.data.message

                setUnexpectedErrors(errorMessages || [])

                setSubmitting(false)
            }
        })
        .catch((error: any) => {
            console.log(error)

            setSubmitting(false)
        })
}

function resetForm(setInitialValues: Function) {
    setInitialValues({})
}

function resetRequestErrors(setRequestErrors: Function) {
    setRequestErrors({})
}

const StoreForm = (props: StoreFormProps) => {
    const { t } = useTranslation()

    const [rehydrateForm] = useState<boolean>(false)
    const [initialStep] = useState<number>(1)
    const [initialValues, setInitialValues] = useState<any>({})
    const [submitting, setSubmitting] = useState<boolean>(false)
    const [requestErrors, setRequestErrors] = useState({})
    const [unexpectedErrors, setUnexpectedErrors] = useState<string[]>([])
    const [municipalities, setMunicipalities] = useState<IMunicipality[]>([])

    useEffect(() => {
        fetchMunicipalities(setMunicipalities, setUnexpectedErrors)
    }, [])

    return (
        <div>
            <Help
                content={props.helpText}
                fullscreen={true}
            />
            <div className='form-wrapper'>
                <PantaForm
                    onSubmit={submit.bind(
                        null,
                        props.myPagesRoute,
                        setSubmitting,
                        setRequestErrors,
                        setUnexpectedErrors
                    )}
                    onCancel={props.onCancel}
                    defaultValues={initialValues}
                    initialStep={initialStep}
                >
                    <PantaFormStep
                        component={Step1}
                        stepProps={
                            {
                                heading: t(
                                    'companyRegistration.customer.store.stepLabel.step1'
                                ),
                                cmsContent: props.cmsContent.step1,
                                rehydrateFormKey: rehydrateForm,
                            } as Step1Props
                        }
                    />
                    <PantaFormStep
                        component={Step2}
                        stepProps={
                            {
                                cmsContent: props.cmsContent.step2,
                                rehydrateFormKey: rehydrateForm,
                                newRegistration: resetForm.bind(
                                    null,
                                    setInitialValues
                                ),
                            } as Step2Props
                        }
                    />
                    <PantaFormStep
                        component={Step3}
                        stepProps={
                            {
                                cmsContent: props.cmsContent.step3,
                                rehydrateFormKey: rehydrateForm,
                                municipalities: municipalities,
                                unexpectedErrors: unexpectedErrors,
                            } as Step3Props
                        }
                    />
                    <PantaFormStep
                        component={Step4}
                        stepProps={
                            {
                                heading: t(
                                    'companyRegistration.customer.store.stepLabel.step4'
                                ),
                                cmsContent: props.cmsContent.step4,
                                rehydrateFormKey: rehydrateForm,
                            } as Step4Props
                        }
                    />
                    <PantaFormStep
                        component={Step5}
                        stepProps={
                            {
                                heading: t(
                                    'companyRegistration.customer.store.stepLabel.step5'
                                ),
                                cmsContent: props.cmsContent.step5,
                                rehydrateFormKey: rehydrateForm,
                                termsAndConditions: props.termsAndConditions,
                                termsAndConditionsButtons: props.termsAndConditionsButtons,
                                submitting: submitting,
                                requestErrors: requestErrors,
                                resetRequestErrors: resetRequestErrors.bind(
                                    null,
                                    setRequestErrors
                                ),
                                unexpectedErrors: unexpectedErrors,
                                getSections: getSections.bind(null, t),
                            } as Step5Props
                        }
                    />
                </PantaForm>
            </div>
        </div>
    )
}

export default StoreForm